import { useMemo, useState } from 'react'
import { ApiError } from '@/shared/api/http'
import { api } from '@/shared/api/endpoints'
import type { Currency, PaymentPageResponse } from '@/shared/api/types'
import { coinsFor } from '@/shared/lib/currencies'
import { crypto } from '@/shared/lib/format'
import { Select, type Option } from '@/shared/ui/Select'
import { Button } from '@/shared/ui/Button'
import { Strip } from '@/shared/ui/Strip'
import styles from './Payment.module.css'

interface Props {
  invoiceId: string
  available: Currency[]
  /** Комиссия сети по монетам, где она есть (сейчас USDT TRC-20). */
  feeEstimates: Record<string, string>
  onSelected: (page: PaymentPageResponse) => void
}

/**
 * Выбор валюты и сети — единственное решение плательщика.
 *
 * Два поля, а не одно, потому что USDT живёт в двух сетях с
 * несовместимыми адресами, и перевод в чужую сеть не возвращается
 * никак. Подтверждение отдельной кнопкой (а не по клику на монету)
 * тоже не украшение: бекенд выдаёт адрес и запрещает менять валюту
 * после этого — платёж на выданный ранее адрес потерялся бы. Кнопка
 * даёт паузу перед необратимым шагом.
 */
export function PaymentForm({ invoiceId, available, feeEstimates, onSelected }: Props) {
  const coins = useMemo(() => coinsFor(available), [available])
  const [ticker, setTicker] = useState<string | null>(null)
  const [currency, setCurrency] = useState<Currency | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const coin = coins.find((item) => item.ticker === ticker) ?? null
  // У монеты с одной сетью выбирать нечего — подставляем сразу,
  // но поле показываем: плательщик должен ВИДЕТЬ, в какую сеть шлёт.
  const networks = coin?.networks ?? []
  const chosen: Currency | null =
    currency ?? (networks.length === 1 && networks[0] ? networks[0].currency : null)

  /* Без цветной метки: в макете в поле стоит только тикер, и метка
     сдвигала бы «USDT» на 18px вправо. В самом списке различать монеты
     помогает вторая строка с полным названием. */
  const coinOptions: Option[] = coins.map((item) => ({
    value: item.ticker,
    label: item.ticker,
    sub: item.label,
  }))
  const networkOptions: Option[] = networks.map((item) => ({
    value: item.currency,
    label: item.name,
  }))
  /* Строка из макета под выбором сети. Показывается до выдачи адреса,
     чтобы сумма к оплате на следующем экране не выглядела ошибкой
     курса: в TRC-20 к счёту добавляется плата за свод адреса. */
  const fee = chosen ? feeEstimates[chosen] : undefined
  const feeNote = fee && coin ? `Комиссия сети: ${crypto(fee)} ${coin.ticker}` : null

  const submit = async () => {
    if (!chosen || busy) return
    setBusy(true)
    setError(null)
    try {
      onSelected(await api.payments.selectCurrency(invoiceId, chosen))
    } catch (cause) {
      setError(explain(cause))
    } finally {
      setBusy(false)
    }
  }

  if (coins.length === 0) {
    return (
      <Strip tone="warn">Нет доступных сетей для оплаты</Strip>
    )
  }

  return (
    <div className={styles.stack}>
      <Select
        label="Валюта"
        placeholder="Укажите валюту"
        options={coinOptions}
        value={ticker}
        onChange={(value) => {
          setTicker(value)
          setCurrency(null)
          setError(null)
        }}
      />

      {coin ? (
        <Select
          label="Сеть"
          placeholder="Укажите сеть"
          options={networkOptions}
          value={chosen}
          onChange={(value) => setCurrency(value as Currency)}
          disabled={networks.length === 1}
          description={feeNote}
        />
      ) : null}

      <Strip tone="info">Вы платите комиссию сети</Strip>

      {error ? (
        <Strip tone="bad">{error}</Strip>
      ) : null}

      <Button block busy={busy} disabled={!chosen} onClick={() => void submit()}>
        Перейти к оплате
      </Button>
    </div>
  )
}

/**
 * Коды бекенда человеческим языком.
 *
 * «no_available_address» не говорит плательщику ничего, а разница между
 * «возьмите другую сеть» и «заявка истекла» — это разница между
 * состоявшейся покупкой и брошенной корзиной.
 */
function explain(cause: unknown): string {
  if (cause instanceof ApiError) {
    switch (cause.code) {
      case 'no_available_address':
        return 'Нет свободных адресов в этой сети'
      case 'address_quota_exceeded':
        return 'Слишком много заявок в этой сети'
      case 'invoice_expired':
        return 'Время на оплату истекло'
      case 'already_paid':
        return 'Заявка уже оплачена'
      case 'currency_already_selected':
        return 'Сеть уже выбрана'
      case 'rate_unavailable':
        return 'Курс недоступен'
      case 'too_many_requests':
        return 'Слишком много попыток'
      default:
        return cause.message || 'Не удалось выбрать сеть'
    }
  }
  return 'Сервер не отвечает'
}
